import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle } from "lucide-react";
import { motion } from "framer-motion";
import { fadeInUp, slideInLeft, slideInRight, staggerContainer, viewportConfig } from "@/lib/animations";

const whoThisIsFor = [
  "Owners serious about longevity",
  "Leaders tired of guessing",
  "Businesses ready to modernize without chaos",
];

const whatHappensNext = [
  "Confidential assessment call",
  "Structured diagnostic proposal",
  "Clear path forward — with or without us",
];

export const FinalCTASection = () => {
  return (
    <motion.section
      className="section-padding bg-primary text-primary-foreground"
      initial="hidden"
      whileInView="visible"
      viewport={viewportConfig}
      variants={staggerContainer}
    >
      <div className="section-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Column */}
          <motion.div variants={slideInLeft}>
            <p className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-primary-foreground/50 mb-6">
              Start With a Strategic Assessment
            </p>
            <h2 className="heading-2 mb-8">
              The First Step is Clarity
            </h2>

            <div className="space-y-8">
              {/* Who This Is For */}
              <motion.div variants={staggerContainer}>
                <h3 className="font-sans text-sm font-semibold uppercase tracking-wider text-primary-foreground/70 mb-4">
                  Who This Is For
                </h3>
                <ul className="space-y-3">
                  {whoThisIsFor.map((item, index) => (
                    <motion.li
                      key={index}
                      className="flex items-center gap-3"
                      variants={fadeInUp}
                    >
                      <CheckCircle size={18} className="text-primary-foreground/50 flex-shrink-0" />
                      <span className="font-sans text-base text-primary-foreground/90">{item}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>

              {/* What Happens Next */}
              <motion.div variants={staggerContainer}>
                <h3 className="font-sans text-sm font-semibold uppercase tracking-wider text-primary-foreground/70 mb-4">
                  What Happens Next
                </h3>
                <ul className="space-y-3">
                  {whatHappensNext.map((item, index) => (
                    <motion.li
                      key={index}
                      className="flex items-center gap-3"
                      variants={fadeInUp}
                    >
                      <span className="font-sans text-xs text-primary-foreground/50 w-6">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <span className="font-sans text-base text-primary-foreground/90">{item}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </motion.div>

          {/* Right Column - CTA */}
          <motion.div
            className="bg-primary-foreground/5 border border-primary-foreground/10 p-10 text-center"
            variants={slideInRight}
            whileHover={{ scale: 1.02, transition: { duration: 0.25 } }}
          >
            <h3 className="heading-3 mb-4">
              Ready to Begin?
            </h3>
            <p className="body-base text-primary-foreground/70 mb-8">
              Schedule a confidential strategic assessment with our team.
            </p>
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button
                asChild
                className="rounded-none px-10 py-6 bg-primary-foreground text-primary hover:bg-primary-foreground/90 font-sans text-sm font-medium uppercase tracking-wide w-full sm:w-auto"
              >
                <Link to="/request-assessment" className="flex items-center justify-center gap-2">
                  Request a Strategic Assessment
                  <ArrowRight size={16} />
                </Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};
